import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@its-engineering-and-development/ui-foundation/components";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import { DataTableShell } from "@/components/base/data-table/data-table";
import { ACTIONS } from "@/auth";
import { useCan } from "@/hooks/use-can";
import { loadSearchHistory, normalizeFilterValue, removeFromHistory, saveSearchHistory } from "@/hooks/use-column-filter";
import { ColumnFilterPopover } from "@/components/base/column-filter-popover";
import type { SortDirection } from "@/components/base/column-filter-popover";
import { ColumnHeader } from "@/components/base/column-header";
import {
    useExceptionPolicies,
    useCreatePolicy,
    useGetPolicyById,
    useUpdatePolicy,
    type ExceptionPoliciesColumnFilter,
    type ExceptionPoliciesSortBy,
    type ExceptionPoliciesSortOrder,
    type CreatePolicyInput,
    type UpdatePolicyInput,
} from "@app/hooks/use-policy";
import { useActivePolicyCategories, useAllPolicyCategories } from "@app/hooks/use-categories";
import { isMileage } from "@app/constants/expense-categories";
import { PolicyPageError } from "./policy-table-header";
import { PolicyStatusBadge } from "./policy-status-badge";
import { CreateRuleDrawer, buildCelExpression, policyToFormState } from "./create-rule-drawer";
import type { CreateRuleFormState } from "./create-rule-drawer";

const EXCEPTIONS_SEARCH_HISTORY_KEY = "expense-admin:exceptions-policy-column-search-history";

type ExceptionColumnKey = "ruleName" | "status" | "category" | "expression" | "amount" | "entryType" | "response";

const FILTERABLE_COLUMNS = new Set<ExceptionColumnKey>(["ruleName", "status"]);
const SORTABLE_COLUMNS = new Set<ExceptionColumnKey>(["ruleName"]);

// Maps UI column key → API sort field name
const COLUMN_SORT_FIELD: Partial<Record<ExceptionColumnKey, ExceptionPoliciesSortBy>> = {
    ruleName: "name",
};

const EXCEPTION_STATUS_FILTER_OPTIONS = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
];

function CategoryCell({ category, categoryInactive }: { category: string; categoryInactive: boolean }) {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || expanded) return;
        setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [category, expanded]);

    return (
        <div className="flex flex-col items-start gap-1">
            <span className="flex items-start gap-1.5">
                <span ref={textRef} className={expanded ? undefined : "line-clamp-3"}>
                    {category}
                </span>
                {categoryInactive && (
                    <span
                        title="One or more categories are inactive"
                        className="mt-0.5 inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                    >
                        Inactive
                    </span>
                )}
            </span>
            {isOverflowing && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((v) => !v);
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                    {expanded ? "Show less" : "+ More"}
                </button>
            )}
        </div>
    );
}

function buildApiFilter(applied: Partial<Record<ExceptionColumnKey, string>>): ExceptionPoliciesColumnFilter {
    const filter: ExceptionPoliciesColumnFilter = {};
    if (applied.ruleName) filter.name = applied.ruleName;
    if (applied.status) filter.status = applied.status;
    return filter;
}

function IconDownload() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 3v12m0 0-4-4m4 4 4-4M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
        </svg>
    );
}

function IconPlus() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 5v14M5 12h14" />
        </svg>
    );
}

export function ExceptionsPolicyPage() {
    const { categoryMap } = useAllPolicyCategories();
    const { categories, isLoading: isLoadingCategories, error: categoriesError } = useActivePolicyCategories();
    const { policies, pageInfo, currentPage, sort, isLoading, error, setPage, setSort, setColumnFilter, refresh } = useExceptionPolicies(categoryMap);
    const { createPolicy } = useCreatePolicy();
    const { getPolicyById } = useGetPolicyById();
    const { updatePolicy } = useUpdatePolicy();
    const { showSuccess, showError } = useNotifications();
    const canWrite = useCan(ACTIONS.policy.exceptions.write);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // ── Edit (update) drawer state ────────────────────────────────────────────
    const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
    const [editingFormState, setEditingFormState] = useState<CreateRuleFormState | null>(null);
    const [isEditLoading, setIsEditLoading] = useState(false);

    // ── Filter UI state ────────────────────────────────────────────────────────
    const [activeFilterColumn, setActiveFilterColumn] = useState<ExceptionColumnKey | null>(null);
    const [activeFilterAnchor, setActiveFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterInput, setFilterInput] = useState("");
    const [appliedFilters, setAppliedFilters] = useState<Partial<Record<ExceptionColumnKey, string>>>({});
    const [searchHistory, setSearchHistory] = useState<Record<string, string[]>>(() => loadSearchHistory(EXCEPTIONS_SEARCH_HISTORY_KEY));

    const persistHistory = useCallback((next: Record<string, string[]>) => {
        setSearchHistory(next);
        saveSearchHistory(EXCEPTIONS_SEARCH_HISTORY_KEY, next);
    }, []);

    // ── Pagination adapter ────────────────────────────────────────────────────
    const tablePageInfo: TablePageInfo | null = pageInfo
        ? {
              totalCount: pageInfo.totalCount,
              offset: currentPage * (pageInfo.limit || 10),
              limit: pageInfo.limit || 10,
              hasNextPage: pageInfo.hasNextPage,
          }
        : null;

    // ── Sort handler (asc → desc → clear) ────────────────────────────────────
    const handleSort = useCallback(
        (columnKey: ExceptionColumnKey) => {
            const sortBy = COLUMN_SORT_FIELD[columnKey];
            if (!sortBy) return;
            let next: { sortBy: ExceptionPoliciesSortBy; sortOrder: ExceptionPoliciesSortOrder } | null;
            if (sort?.sortBy !== sortBy) {
                next = { sortBy, sortOrder: "asc" };
            } else if (sort.sortOrder === "asc") {
                next = { sortBy, sortOrder: "desc" };
            } else {
                next = null;
            }
            setSort(next);
        },
        [sort, setSort],
    );

    // ── Filter handlers ───────────────────────────────────────────────────────
    const openColumnFilter = useCallback(
        (column: ExceptionColumnKey, anchorEl: HTMLElement) => {
            if (activeFilterColumn === column) {
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
                return;
            }
            setActiveFilterColumn(column);
            setActiveFilterAnchor(anchorEl);
            setFilterInput(appliedFilters[column] ?? "");
        },
        [activeFilterColumn, appliedFilters],
    );

    const closeFilter = useCallback(() => {
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, []);

    const applyColumnFilter = useCallback(
        (value?: string) => {
            if (!activeFilterColumn) return;
            const val = normalizeFilterValue(value ?? filterInput);
            const next = { ...appliedFilters };
            if (val) {
                next[activeFilterColumn] = val;
                persistHistory({
                    ...searchHistory,
                    [activeFilterColumn]: [val, ...(searchHistory[activeFilterColumn] ?? []).filter((v) => v !== val)],
                });
            } else {
                delete next[activeFilterColumn];
            }
            setAppliedFilters(next);
            closeFilter();
            setColumnFilter(buildApiFilter(next));
        },
        [activeFilterColumn, filterInput, appliedFilters, searchHistory, persistHistory, closeFilter, setColumnFilter],
    );

    const clearColumnFilter = useCallback(() => {
        if (!activeFilterColumn) return;
        const next = { ...appliedFilters };
        delete next[activeFilterColumn];
        setAppliedFilters(next);
        closeFilter();
        setColumnFilter(buildApiFilter(next));
    }, [activeFilterColumn, appliedFilters, closeFilter, setColumnFilter]);

    const removeSaved = useCallback(
        (value: string) => {
            if (!activeFilterColumn) return;
            persistHistory(removeFromHistory(searchHistory, activeFilterColumn, value));
        },
        [activeFilterColumn, searchHistory, persistHistory],
    );

    // ── Column header renderer ────────────────────────────────────────────────
    const colHeader = (
        key: ExceptionColumnKey,
        label: string,
        opts?: { filterOptions?: Array<{ label: string; value: string }>; inputType?: "text" | "date"; hint?: string },
    ) => {
        const isSortable = SORTABLE_COLUMNS.has(key);
        const isSortActive = isSortable && sort?.sortBy === COLUMN_SORT_FIELD[key];
        const isFilterable = FILTERABLE_COLUMNS.has(key);
        const hasActiveFilter = key in appliedFilters;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable={isFilterable}
                isFilterActive={hasActiveFilter}
                isFilterOpen={activeFilterColumn === key}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl)}
                isSortable={isSortable}
                isSortActive={isSortActive}
                sortDirection={isSortActive ? (sort!.sortOrder as SortDirection) : undefined}
                onSort={() => handleSort(key)}
            />,
            isFilterable ? (
                <ColumnFilterPopover
                    key={`${key}-filter`}
                    isOpen={activeFilterColumn === key}
                    anchorEl={activeFilterAnchor}
                    columnLabel={label}
                    hint={opts?.hint}
                    inputType={opts?.inputType ?? "text"}
                    options={opts?.filterOptions}
                    searchValue={filterInput}
                    savedValues={opts?.filterOptions ? undefined : (searchHistory[key] ?? [])}
                    onSearchChange={setFilterInput}
                    onClose={closeFilter}
                    onApply={applyColumnFilter}
                    onClear={clearColumnFilter}
                    onRemoveSavedValue={removeSaved}
                />
            ) : null,
        ];
    };

    // Mileage is not selectable per-category, so "All Categories" must exclude it explicitly.
    const buildRulesPayload = useCallback(
        (state: CreateRuleFormState): CreatePolicyInput["rules"] => {
            const isAll = state.categoryMode === "all";
            const mileageCategoryId = isAll ? categories.find((c) => isMileage(c.legacyId))?.categoryId : undefined;
            return {
                type: "cel",
                categoryIds: isAll ? ["*"] : state.selectedCategories,
                ...(mileageCategoryId ? { excludeCategoryIds: [mileageCategoryId] } : {}),
                expression: buildCelExpression(state),
            };
        },
        [categories],
    );

    const ensureCategoriesReady = useCallback(() => {
        if (isLoadingCategories) {
            showError("Categories still loading", "Please wait a moment and try again.");
            return false;
        }
        if (categoriesError) {
            showError("Could not load categories", "Failed to load category data. Please refresh the page and try again.");
            return false;
        }
        return true;
    }, [isLoadingCategories, categoriesError, showError]);

    // ── Drawer handlers ────────────────────────────────────────────────────
    const handleOpenDrawer = useCallback(() => setIsDrawerOpen(true), []);
    const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), []);
    const handleCloseUpdateDrawer = useCallback(() => {
        setEditingPolicyId(null);
        setEditingFormState(null);
        setIsEditLoading(false);
    }, []);

    const handleRowClick = useCallback(
        async (policyId: string) => {
            // Open the drawer immediately — loading state shown inside
            setEditingPolicyId(policyId);
            setEditingFormState(null);
            setIsEditLoading(true);
            try {
                const policy = await getPolicyById(policyId);
                if (!policy) throw new Error("Policy not found");
                setEditingFormState(policyToFormState(policy));
            } catch {
                showError("Failed to load rule", "Could not fetch the rule details. Please try again.");
                setEditingPolicyId(null);
            } finally {
                setIsEditLoading(false);
            }
        },
        [getPolicyById, showError],
    );

    const handleSubmitRule = useCallback(
        async (state: CreateRuleFormState) => {
            if (!ensureCategoriesReady()) return;
            const input: CreatePolicyInput = {
                name: state.ruleName,
                description: state.description || undefined,
                policyType: "exceptions",
                status: state.isActive ? "active" : "inactive",
                rules: buildRulesPayload(state),
                actions: [{ type: state.responseType === "require_explanation" ? "requireExplanation" : "highlight" }],
            };
            try {
                await createPolicy(input);
                handleCloseDrawer();
                refresh();
                showSuccess("Rule created", `"${state.ruleName}" was added to the exceptions policy.`);
            } catch {
                showError("Failed to create rule", "Something went wrong. Please try again.");
            }
        },
        [createPolicy, ensureCategoriesReady, buildRulesPayload, handleCloseDrawer, refresh, showSuccess, showError],
    );

    const handleUpdateRule = useCallback(
        async (state: CreateRuleFormState) => {
            if (!editingPolicyId || !ensureCategoriesReady()) return;
            const input: UpdatePolicyInput = {
                name: state.ruleName,
                description: state.description || undefined,
                status: state.isActive ? "active" : "inactive",
                rules: buildRulesPayload(state),
                actions: [{ type: state.responseType === "require_explanation" ? "requireExplanation" : "highlight" }],
            };
            try {
                await updatePolicy(editingPolicyId, input);
                handleCloseUpdateDrawer();
                refresh();
                showSuccess("Rule updated", `"${state.ruleName}" was updated successfully.`);
            } catch {
                showError("Failed to update rule", "Something went wrong. Please try again.");
            }
        },
        [editingPolicyId, updatePolicy, ensureCategoriesReady, buildRulesPayload, handleCloseUpdateDrawer, refresh, showSuccess, showError],
    );

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Exceptions Policy</h1>
                        <p className="mt-2 text-sm text-gray-600">Configure expense policy rules and automated responses for specific conditions.</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        <Button color="secondary" size="md" isDisabled title="Export not yet supported" iconLeading={IconDownload}>
                            Export
                        </Button>
                        {canWrite && (
                            <Button color="primary" size="md" onPress={handleOpenDrawer} iconLeading={IconPlus}>
                                Add Rule
                            </Button>
                        )}
                    </div>
                </div>

                {error && <PolicyPageError message={error} />}

                <DataTableShell
                    tabName="exceptions policy"
                    isLoading={isLoading}
                    loadingDescription="Please wait while the exception policies load."
                    minWidth="1100px"
                    pageInfo={tablePageInfo}
                    onPageChange={setPage}
                    isEmpty={policies.length === 0}
                    hasActiveFilters={Object.keys(appliedFilters).length > 0}
                    emptyMessage={Object.keys(appliedFilters).length > 0 ? "No exceptions match the current filters." : "No exceptions found."}
                    header={
                        <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left">
                            <tr>
                                {colHeader("ruleName", "Rule name")}
                                {colHeader("status", "Status", { filterOptions: EXCEPTION_STATUS_FILTER_OPTIONS })}
                                {colHeader("category", "Category")}
                                {colHeader("expression", "Expression")}
                                {colHeader("amount", "Amount")}
                                {colHeader("entryType", "Entry type")}
                                {colHeader("response", "Response")}
                            </tr>
                        </thead>
                    }
                >
                    <tbody className="divide-y divide-gray-100">
                        {policies.map((rule) => (
                            <tr key={rule.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(rule.id)}>
                                <td className="px-4 py-3 font-medium text-blue-600">{rule.ruleName}</td>
                                <td className="px-4 py-3">
                                    <PolicyStatusBadge status={rule.status} />
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                    <CategoryCell category={rule.category} categoryInactive={rule.categoryInactive} />
                                </td>
                                <td className="px-4 py-3 text-gray-700">{rule.expression}</td>
                                <td className="px-4 py-3 text-gray-700">{rule.amount}</td>
                                <td className="px-4 py-3 text-gray-700">{rule.entryType}</td>
                                <td className="px-4 py-3 text-gray-700">{rule.response}</td>
                            </tr>
                        ))}
                    </tbody>
                </DataTableShell>
            </div>

            {isDrawerOpen && createPortal(<CreateRuleDrawer onClose={handleCloseDrawer} onSubmit={handleSubmitRule} canEdit={canWrite} />, document.body)}
            {editingPolicyId &&
                createPortal(
                    <CreateRuleDrawer
                        onClose={handleCloseUpdateDrawer}
                        onSubmit={handleUpdateRule}
                        policyId={editingPolicyId}
                        initialState={editingFormState ?? undefined}
                        isLoading={isEditLoading}
                        canEdit={canWrite}
                    />,
                    document.body,
                )}
        </>
    );
}
